import { Box, Button, Upload } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import CreateFolderModal from "./CreateFolderModal";
import FileSharingQuotaIndicator from "./FileSharingQuotaIndicator";
import FileSharingTable from "./FileSharingTable";
import { FileTreeNodeSortKey, isFileTreeNodeSortKey } from "./fileTreeUtils";
import FileUploadModal from "./FileUploadModal";
import { useAbortControllers } from "./useAbortControllers";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { FILE_SHARING_ROUTE } from "components/router/Routes";
import { AUTH_FILESHARE_CREATE } from "domain/authority";
import { FileShareQuotaResponseDto, SharedFile } from "domain/fileSharing";
import { fileSharingService } from "services/file-sharing/FileSharingService";
import { userSessionService } from "services/user/UserSessionService";
import { logger } from "utils/logging";
import { useTableRefresh } from "utils/useTableRefresh";

import testIds from "testIds.json";

export type FilterType = "all" | "mine";

export default function FileSharingView(): JSX.Element {
    const { t } = useTranslation();
    const abortControllers = useAbortControllers();

    const [files, setFiles] = React.useState<SharedFile[]>([]);
    const [loaded, setLoaded] = React.useState(false);
    const [failureMessage, setFailureMessage] = React.useState<string | undefined>();
    const [filter, setFilter] = React.useState<FilterType>("all");
    const [cursor, setCursor] = React.useState<string | undefined>();
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [uploadOpen, setUploadOpen] = React.useState(false);
    const [createFolderModalOpen, setCreateFolderModalOpen] = React.useState(false);
    const [sortColumn, setSortColumn] = React.useState<FileTreeNodeSortKey | undefined>();
    const [sortAscending, setSortAscending] = React.useState(true);
    const [quota, setQuota] = React.useState<FileShareQuotaResponseDto | undefined>();
    const { refreshCount, onHandleRefresh } = useTableRefresh();

    const existingPaths = React.useMemo(() => {
        const pathSet = new Set<string>();
        for (const file of files) {
            const path = file.directoryPath;
            if (!path || path === "/") {
                continue;
            }
            const segments = path.split("/");
            for (let i = 1; i <= segments.length; i++) {
                pathSet.add(segments.slice(0, i).join("/"));
            }
        }
        return Array.from(pathSet).sort();
    }, [files]);

    const fetchAbortControllerRef = React.useRef<AbortController | undefined>();

    const fetchFiles = React.useCallback(() => {
        fetchAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;
        abortControllers.push(abortController);
        setLoaded(false);
        setFailureMessage(undefined);
        setCursor(undefined);
        setLoadingMore(false);
        fileSharingService
            .listFiles({ userFilesOnly: filter === "mine", abortController })
            .then((response) => {
                setFiles(response.files);
                setCursor(response.cursor);
                setLoaded(true);
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch shared files", error);
                    setFailureMessage(t("Common.resultErrorMessage"));
                    setLoaded(true);
                }
            });
    }, [filter, t]);

    const handleLoadMore = React.useCallback(() => {
        if (!cursor) {
            return;
        }
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setLoadingMore(true);
        fileSharingService
            .listFiles({ userFilesOnly: filter === "mine", cursor, abortController })
            .then((response) => {
                setFiles((prev) => [...prev, ...response.files]);
                setCursor(response.cursor);
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to load more shared files", error);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoadingMore(false);
                }
            });
    }, [filter, cursor]);

    const handleSortChange = React.useCallback((column: string, ascending: boolean): void => {
        if (isFileTreeNodeSortKey(column)) {
            setSortColumn(column);
        }
        setSortAscending(ascending);
    }, []);

    const handleSortDisable = React.useCallback((): void => {
        setSortColumn(undefined);
    }, []);

    const fetchQuotaAbortControllerRef = React.useRef<AbortController | undefined>();

    const fetchQuota = React.useCallback(() => {
        fetchQuotaAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        fetchQuotaAbortControllerRef.current = abortController;
        fileSharingService
            .fetchQuota(abortController)
            .then(setQuota)
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch file sharing quota", error);
                }
            });
    }, []);

    React.useEffect(() => {
        fetchFiles();
        return () => abortControllers.abortAll();
    }, [fetchFiles, refreshCount]);

    React.useEffect(() => {
        fetchQuota();
        return () => fetchQuotaAbortControllerRef.current?.abort();
    }, [fetchQuota, refreshCount]);

    const canUpload = userSessionService.userHasAllAuthorities([AUTH_FILESHARE_CREATE]);
    const hasQuota = quota?.quota !== undefined;

    return (
        <ApplicationLayout
            viewTitle={t("FileSharing.title")}
            mainRoute={FILE_SHARING_ROUTE}
            enableRefresh
            onHandleRefresh={onHandleRefresh}
            viewActions={
                canUpload || hasQuota ? (
                    <>
                        {quota !== undefined && quota.quota !== undefined && quota.consumption !== undefined && (
                            <FileSharingQuotaIndicator quota={quota.quota} consumption={quota.consumption} />
                        )}
                        {canUpload && (
                            <Box gap="Xs" verticalAlignment="Center">
                                <Button
                                    testId={testIds.workArea.fileSharing.createFolderButton}
                                    variant="Secondary"
                                    onClick={() => setCreateFolderModalOpen(true)}
                                >
                                    {t("FileSharing.createFolder")}
                                </Button>

                                <Button
                                    testId={testIds.workArea.fileSharing.uploadButton}
                                    variant="Primary"
                                    icon={<Upload />}
                                    onClick={() => setUploadOpen(true)}
                                >
                                    {t("FileSharing.uploadFile")}
                                </Button>
                            </Box>
                        )}
                    </>
                ) : undefined
            }
            view={
                <>
                    <FileSharingTable
                        files={files}
                        existingPaths={existingPaths}
                        loaded={loaded}
                        failureMessage={failureMessage}
                        filter={filter}
                        onFilterChange={setFilter}
                        cursor={cursor}
                        onLoadMore={handleLoadMore}
                        loadingMore={loadingMore}
                        onRefresh={fetchFiles}
                        onRefreshQuota={fetchQuota}
                        sortCallbacks={{ onChange: handleSortChange, onDisable: handleSortDisable }}
                        sortColumn={sortColumn}
                        sortAscending={sortAscending}
                    />
                    <FileUploadModal
                        isOpen={uploadOpen}
                        onClose={() => setUploadOpen(false)}
                        onUploadComplete={onHandleRefresh}
                        existingPaths={existingPaths}
                        quota={quota}
                    />
                    <CreateFolderModal
                        isOpen={createFolderModalOpen}
                        existingPaths={existingPaths}
                        onClose={() => setCreateFolderModalOpen(false)}
                        onCreated={onHandleRefresh}
                    />
                </>
            }
        />
    );
}
